import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { ActionPlanPreview } from '.';
import { Button } from '../../../components/common';
import { DropdownOptionValue } from '../../../components/common/Dropdown';
import {
  FormActions,
  InputField,
  TextAreaElement,
  TimeDropdown,
  DayDropdown,
  MonthDropdown,
} from '../../../components/common/forms';
import { scrollToTop } from '../../../utils';
import { FormPageContainer } from '../FormPageContainer';
import { ContentBlock, Form, FormRow, RequiredIndicator } from '../layoutComponents';
import { H2, H3 } from '../Text';
import { ActionPlanFormState, schema } from './schema';
import { YearDropdown } from './YearDropdown';
import { NoticeForm, NoticeFormType, NoticeRequestBody } from '../../../types/notices';
import { NotificationType, useAppDispatch } from '../../../context/appContext';
import { createNotice, publishNotice, updateNotice } from '../../../apis_kenno';
import { showNotification } from '../../../context/appActions';
import Submitted from '../Submitted';
import FormError from '../../../components/common/forms/FormError';

const ActivitiesSummaryRow = styled(FormRow)`
  font-size: 18px;
  font-weight: 700;
  font-family: ${(props) => props.theme.text.secondaryFont};
  line-height: 27px;
  justify-content: space-between;
`;

const ActivitiesContent = styled(ContentBlock)``;

interface ActionPlanProps {
  updateForm?: NoticeForm;
}

export const ActionPlan: React.FC<ActionPlanProps> = ({ updateForm }) => {
  const appDispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialId = updateForm ? updateForm.id : null;
  const [formId, setFormId] = useState<string | null>(initialId);
  const [published, setPublished] = useState(false);

  const initialValues = {
    year: new Date().getFullYear(), // Set to current year
    activities: [{ name: '', responsiblePersons: '', budgetEstimate: 0.0 }],
  };

  const formMethods = useForm<ActionPlanFormState>({
    resolver: zodResolver(schema),
    defaultValues: updateForm ? { ...initialValues, ...updateForm.formData } : { ...initialValues },
  });

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const t = useMessageGetter('forms.actionPlan');
  const tCommon = useMessageGetter('forms');

  const handleSubmitFinal = async (data: ActionPlanFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    const body: NoticeRequestBody = {
      formData: data,
      formType: NoticeFormType.ActionPlan,
    };

    try {
      let id = formId;
      if (id) {
        await updateNotice(id, body);
      } else {
        id = (await createNotice(body)).id;
      }
      await publishNotice(id);
      handleEmptyForm();
      setPublished(true);
      scrollToTop();
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.publish'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = async (data: ActionPlanFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    const body: NoticeRequestBody = {
      formData: data,
      formType: NoticeFormType.ActionPlan,
    };

    try {
      if (!formId) {
        const actionPlan = await createNotice(body);
        setFormId(actionPlan.id);
      } else {
        await updateNotice(formId, body);
      }
      await showNotification(appDispatch, tCommon('info.saved'), NotificationType.SUCCESS);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.save'));
    } finally {
      setIsBusy(false);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'activities',
  });

  const handleAddNewEvent = () => {
    append({
      name: '',
      // These typecastings are a workaround for typescript error stating that day and month are missing
      // (we do not want to default them to anything)
      // This works differently than useForm's defaultValues in which the values can be null or undefined.
      // Unfortunately append function is strictly following the schema in which the values are required.
      // Discussion about the flaw here: https://github.com/orgs/react-hook-form/discussions/10211
      day: undefined as unknown as number,
      month: undefined as unknown as number,
      time: undefined,
      responsiblePersons: '',
      budgetEstimate: 0.0,
    });
  };

  const handleEmptyForm = () => {
    formMethods.reset();
  };

  const handleTogglePreview = () => {
    setPreviewOpen(!previewOpen);
    scrollToTop();
  };

  const translationPrefix = 'forms.actionPlan';

  const activities = formMethods.watch('activities');
  const selectedYear = formMethods.watch('year');

  const totalBudgetEstimate = activities.reduce((sum, obj) => sum + obj.budgetEstimate, 0).toFixed(2);

  const totalEventCountText =
    activities.length === 1
      ? t('eventCount', { count: activities.length })
      : t('eventCount_plural', { count: activities.length });

  return (
    <FormPageContainer<ActionPlanFormState> formMethods={formMethods}>
      {!previewOpen && <H2>{t('title')}</H2>}
      {published ? (
        <Submitted message={tCommon('info.published')} />
      ) : (
        <Form>
          {previewOpen ? (
            <ActionPlanPreview formData={formMethods.getValues()} />
          ) : (
            <>
              <span>
                {t('description.part1')} (<RequiredIndicator>*</RequiredIndicator>) {t('description.part2')}
              </span>
              <ActivitiesContent>
                <InputField<ActionPlanFormState, DropdownOptionValue>
                  translationPrefix={translationPrefix}
                  fieldPath="year"
                  required
                  inputElementRenderer={(props) => <YearDropdown {...props} />}
                />
                <H3>{t('plannedActivities.title')}</H3>
                {t('plannedActivities.description')}

                {fields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: '1rem' }}>
                    <InputField<ActionPlanFormState, string>
                      fieldPath={`activities.${index}.name`}
                      translationPrefix={translationPrefix}
                      type="text"
                      required
                    />
                    <FormRow>
                      <InputField<ActionPlanFormState, DropdownOptionValue>
                        translationPrefix={translationPrefix}
                        fieldPath={`activities.${index}.month`}
                        inputElementRenderer={(props) => <MonthDropdown {...props} />}
                        required
                      />
                      <InputField<ActionPlanFormState, DropdownOptionValue>
                        translationPrefix={translationPrefix}
                        fieldPath={`activities.${index}.day`}
                        inputElementRenderer={(props) => {
                          const month = formMethods.watch(`activities.${index}.month`);
                          return <DayDropdown {...props} year={selectedYear} month={month} />;
                        }}
                      />
                      <InputField<ActionPlanFormState, DropdownOptionValue>
                        translationPrefix={translationPrefix}
                        fieldPath={`activities.${index}.time`}
                        inputElementRenderer={(props) => <TimeDropdown {...props} />}
                      />
                    </FormRow>
                    <InputField<ActionPlanFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath={`activities.${index}.responsiblePersons`}
                      required
                      inputElementRenderer={(props) => (
                        <TextAreaElement placeholder={t('activities.responsiblePersonsPlaceholder')} {...props} />
                      )}
                    />
                    <InputField<ActionPlanFormState, number>
                      translationPrefix={translationPrefix}
                      fieldPath={`activities.${index}.budgetEstimate`}
                      type="number"
                      step="0.01"
                    />
                    {fields.length > 1 && (
                      <Button primary onClick={() => remove(index)}>
                        {t('removeEvent')}
                      </Button>
                    )}
                  </div>
                ))}
                <FormRow>
                  <Button type="button" icon="plus" onClick={handleAddNewEvent}>
                    {t('addNewEvent')}
                  </Button>
                </FormRow>

                <ActivitiesSummaryRow>
                  <span>{totalEventCountText} </span>
                  <span>{t('totalBudgetEstimateWithAmount', { amount: totalBudgetEstimate })}</span>
                </ActivitiesSummaryRow>
              </ActivitiesContent>
            </>
          )}
          {submitFailed && <FormError message={errorMessage} />}
          <FormActions
            handleEmptyForm={handleEmptyForm}
            handleTogglePreview={handleTogglePreview}
            handleSave={formMethods.handleSubmit(handleSave)}
            handleSubmitFinal={formMethods.handleSubmit(handleSubmitFinal)}
            previewOpen={previewOpen}
            disabled={isBusy}
          />
        </Form>
      )}
    </FormPageContainer>
  );
};
